import { useCallback, useState } from 'react';

/**
 * A boolean the agent chose, remembered in this browser ('1' / '0' under `key`).
 *
 * A per-viewer CONVENIENCE only (is the contact panel collapsed, is a section open): it is never state anything
 * else may depend on. Every storage access is wrapped, because localStorage throws in a private window or with
 * site data blocked, and a panel must render correctly without it - it simply forgets, and `initial` applies.
 *
 * An unset (or unreadable) key yields `initial`; a stored '0' is a real answer, not "unset", which is what lets a
 * default-OPEN section stay closed once an agent closed it.
 */
export function usePersistedFlag(key: string, initial: boolean): [boolean, () => void] {
  const [value, setValue] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? initial : raw === '1';
    } catch {
      return initial;
    }
  });

  const toggle = useCallback(() => {
    setValue((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(key, next ? '1' : '0');
      } catch {
        /* a private-mode browser must not break the panel */
      }
      return next;
    });
  }, [key]);

  return [value, toggle];
}
